import {
  Breadcrumbs,
  BreadcrumbSeperator,
  BreadcrumbTitle,
} from "@/components/Breadcrumbs/Breadcrumbs";
import { useIsModelReadOnly } from "@/hooks/useFinancialDeckPermissions";
import { dealModel, DealRecord } from "@/models/deal";
import { getCurrentFinancialDeckEntity } from "@/models/financialDecks";
import { userIsCreatorConstraint } from "@/orm/model";
import { Route } from "@/paths";
import { Group, TextInput, TextInputProps } from "@mantine/core";
import { useDocumentTitle } from "@mantine/hooks";
import { useAtomValue } from "jotai";
import { memo, useEffect, useMemo, useState } from "react";
import { generatePath } from "react-router-dom";

export const LeftNav = memo(() => {
  const [isModelShared] = useIsModelReadOnly();

  return (
    <Group
      style={{
        borderRight: "1px solid #DDDDDD",
      }}
      mr="xs"
    >
      <Breadcrumbs>
        {!isModelShared && <DealBreadcrumb />}
        <NameInput />
      </Breadcrumbs>
    </Group>
  );
});

const DealBreadcrumb = () => {
  const deckEntity = getCurrentFinancialDeckEntity();
  const atom = useMemo(() => {
    return deckEntity.getObserveManyOwnersAtom<DealRecord>(
      dealModel,
      "belongsToDealIds",
      {
        constraints: [userIsCreatorConstraint()],
      },
    );
  }, []);

  const { entities: deals } = useAtomValue(atom);

  const dealPath = generatePath(Route.Deal, {
    dealId: deals?.[0]?.id || "missing-deal-id",
  });

  if (!deals) return null;

  return (
    <>
      <BreadcrumbTitle to={dealPath} c="blue" visibleFrom="md">
        Deal
      </BreadcrumbTitle>
      <BreadcrumbSeperator visibleFrom="md" />
    </>
  );
};

export const NameInput = (props: TextInputProps) => {
  const [isModelShared] = useIsModelReadOnly();
  const financialDeckEntity = getCurrentFinancialDeckEntity();

  const [inputVal, setInputValue] = useState("");

  useEffect(() => {
    financialDeckEntity.hydrate().then((result) => {
      setInputValue(result.name);
    });
  }, []);

  const breadcrumbWidth = 174;

  useDocumentTitle(`ETA IQ - Model (${inputVal})`);

  return (
    <TextInput
      disabled={isModelShared}
      //leftSection={<LuFileSpreadsheet />}
      maw={200}
      w={{
        xl: `calc(((100vw - 845px)/2) - ${breadcrumbWidth}px)`,
        lg: `calc(((100vw - 664px)/2) - ${breadcrumbWidth}px)`,

        base: `calc(((100vw - 225px)/2) - ${breadcrumbWidth - 70}px)`,
      }}
      ml={-4}
      onChange={(e) => {
        financialDeckEntity.update({ name: e.currentTarget.value });
        setInputValue(e.currentTarget.value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      value={inputVal}
      fw={600}
      flex={1}
      placeholder="Model Name"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      styles={{
        input: {
          textOverflow: "ellipsis",
          background: "transparent",
          minHeight: 10,
          height: 30,
        },
      }}
      {...props}
    />
  );
};
